import React, { useRef, useState, useEffect } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
  Polygon,
} from "@react-google-maps/api";
import indiaShape from "../../../src/assets/geoJsonData/india-composite.json";
import useProcessGeoData from "../cdm/AssemblyMap/useProcessGeoData";
import useProcessGeoDataParliamentary from "../cdm/Parliamentary/ParliamentaryMap/useProcessGeoData"
import useGeoData from "../cdm/AssemblyMap/useGeoData";
import { lib, initialZoom, indiaBounds } from "../../utils/mapConstants";
import { mapStyles } from "../../utils/mapStyles";
import kMeans from "../../utils/clusterMarker";
import useConstituencyData from "../cdm/hooks/useConstituencyData";
import { FullscreenOutlined, CloseOutlined, DownOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { Tooltip } from "antd";
import useAxiosInstance from "../../lib/useAxiosInstance";
import ApprovalRatingMap from "./ApprovalRatingMap";
import GmsFullScreenMap from "./GmsFullScreenMap";
import useGetGrievances from "../../Pages/gms/MapBox/useGetGrievances";
import MarkerInfoContent from "../../Pages/gms/MapBox/MarkerInfoContent";
import ClusterMarker from "./GmsMap/ClusterMarker";
import SurveyAnalyticsMap from "./SurveyAnalytics/SurveyAnalyticsMap";
import NyayePanchayatMap from "./NyayePanchayat/NyayePanchayatMap";
import QuantitativeSurveyMap from "./QuantitativeSurvey/QuantitativeSurveyMap";
import { useTranslation } from "react-i18next";

const extractPolygonRings = (geometry) => {
  if (!geometry?.type || !Array.isArray(geometry?.coordinates)) {
    return [];
  }

  if (geometry.type === "Polygon") {
    return [geometry.coordinates];
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates;
  }

  return [];
};

const OverviewMap = ({ constituencyTableData, isDataAvailable }) => {
  const { t } = useTranslation();
  const axiosInstance = useAxiosInstance();
  const constituencyDataString = JSON.parse(
    localStorage.getItem("constituencyData")
  );
  const themes = localStorage.getItem("themes");
    const userData = JSON.parse(localStorage.getItem("user"));


  // constituencyData from hook (still used elsewhere if needed)
  const { constituencyData } = useConstituencyData(1);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: lib,
  });

  // ------------------------------
  // Polygon states
  // ------------------------------
  // This 'polygonPaths' state remains available for Approval/GMS overrides (unchanged behavior).
  // When approval / GMS fullscreen is active we will display this.
  const [polygonPaths, setPolygonPaths] = useState([]);

  // Separate stores for assembly and parliamentary processed polygons
  const [assemblyPolygonPaths, setAssemblyPolygonPaths] = useState([]);
  const [parliamentaryData, setParliamentaryData] = useState([]); // raw fetched data for parliamentary
  const [parliamentaryPolygonPaths, setParliamentaryPolygonPaths] = useState([]);

  // polyData is from the existing hook (assembly geo data)
  const [polyData, setPolyData] = useGeoData(constituencyData?._id);

  // We'll use the existing hook to process assembly polyData -> assemblyPolygonPaths
  useProcessGeoData(polyData, isLoaded, setAssemblyPolygonPaths);

  // We'll also process parliamentaryData (when fetched) into polygon paths
  useProcessGeoDataParliamentary(parliamentaryData, isLoaded, setParliamentaryPolygonPaths);
  // ------------------------------
  // Other states (unchanged)
  // ------------------------------
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [showApprovalRating, setShowApprovalRating] = useState(false);
  const [showSurveyAnalytics, setShowSurveyAnalytics] = useState(false);
const [surveyResponseData, setSurveyResponseData] = useState(null);
  const [showGmsFullScreen, setShowGmsFullScreen] = useState(false);
  const [selectedPolygon, setSelectedPolygon] = useState(null);
  const [approvalResponseData, setApprovalResponseData] = useState(null);
  const { grievances, loading } = useGetGrievances(isLoaded, null, null, null);

  const [activeMarker, setActiveMarker] = useState(null);
  const [showingInfoWindow, setShowingInfoWindow] = useState(false);

  const [surveySummaries, setSurveySummaries] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [showSurveyDropdown, setShowSurveyDropdown] = useState(false);

  const [showNyayePanchayat, setShowNyayePanchayat] = useState(false);
const [nyayePolygonPaths, setNyayePolygonPaths] = useState([]);

  const [showQuantitativeSurvey, setShowQuantitativeSurvey] = useState(false);
  const [quantitativeSurveyPolygonPaths, setQuantitativeSurveyPolygonPaths] = useState([]);

  const villageData = constituencyTableData?.villageData || [];

  const summary = constituencyTableData?.electoralData?.[0] || {};
  const winner = summary?.partyName || "N/A";
  const votes = summary?.voteShare || "N/A";
  const share =
    summary?.voteSharePercentage != null
      ? Number(summary?.voteSharePercentage).toFixed(2)
      : "N/A";
  const wards = constituencyTableData?.totalVillageCount || "N/A";
  const ps = constituencyTableData?.totalPSCount || "N/A";
  const wardsWon = summary?.noOfVillageWon || "N/A";
  const psWon = summary?.noOfPSWon || "N/A";

  // ------------------------------
  // Map ref + styles
  // ------------------------------
  const mapRef = useRef(null);
  let mapStyle;
  if (themes && themes === "darkMode") {
    mapStyle = [
      {
        elementType: "geometry",
        stylers: [{ color: "#1a2332" }],
      },
      {
        elementType: "labels.text.fill",
        stylers: [{ color: "#ffffff" }],
      },
      {
        elementType: "labels.text.stroke",
        stylers: [{ color: "#1a2332" }],
      },
      {
        featureType: "administrative",
        elementType: "geometry.stroke",
        stylers: [{ color: "#4a6fa5" }, { lightness: -10 }],
      },
      {
        featureType: "administrative.land_parcel",
        elementType: "labels.text.fill",
        stylers: [{ color: "#64779f" }],
      },
      {
        featureType: "administrative.province",
        elementType: "geometry.stroke",
        stylers: [{ color: "#4a6fa5" }],
      },
      {
        featureType: "landscape",
        elementType: "geometry",
        stylers: [{ color: "#263238" }],
      },
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "landscape.man_made",
        elementType: "geometry.stroke",
        stylers: [{ color: "#334e87" }],
      },
      {
        featureType: "landscape.natural",
        elementType: "geometry",
        stylers: [{ color: "#263238" }],
      },
      {
        featureType: "road",
        stylers: [
          {
            visibility: "off",
          },
        ],
      },
      {
        featureType: "road",
        elementType: "labels.icon",
        stylers: [
          {
            visibility: "off",
          },
        ],
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#0e1626" }],
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#4e6d70" }],
      },
    ];
  }
  const buildPolygonPaths = (shapeList = []) => {
  const allPolygons = [];

  shapeList.forEach((shape) => {
    const { boundaries, name } = shape || {};
    if (!boundaries?.type || !boundaries?.coordinates) return;

    if (boundaries.type === "MultiPolygon") {
      boundaries.coordinates.forEach((poly) => {
        const ring = poly[0] || [];
        const coordinates = ring
          .map(([lng, lat]) => {
            const latNum = Number(lat);
            const lngNum = Number(lng);
            return Number.isNaN(latNum) || Number.isNaN(lngNum)
              ? null
              : { lat: latNum, lng: lngNum };
          })
          .filter(Boolean);

        if (coordinates.length) {
          allPolygons.push({ name, coordinate: coordinates });
        }
      });
    }

    if (boundaries.type === "Polygon") {
      boundaries.coordinates.forEach((ring) => {
        const coordinates = ring
          .map(([lng, lat]) => {
            const latNum = Number(lat);
            const lngNum = Number(lng);
            return Number.isNaN(latNum) || Number.isNaN(lngNum)
              ? null
              : { lat: latNum, lng: lngNum };
          })
          .filter(Boolean);

        if (coordinates.length) {
          allPolygons.push({ name, coordinate: coordinates });
        }
      });
    }
  });

  return allPolygons;
};

  useEffect(() => {
    const fetchSurveySummaries = async () => {
      try {
        if (!userData?.surveyId || userData.surveyId.length === 0) return;

        const response = await axiosInstance.post(
          `/api/surveyAnalytics/surveySummary`,
          { surveyIds: userData.surveyId }
        );

        if (response?.data?.status) {
          const surveys = response?.data?.data || [];
          setSurveySummaries(surveys);

          // If only one survey, auto select it
          if (surveys.length === 1) {
            setSelectedSurveyId(surveys[0]._id);
          }
        }
      } catch (error) {
        console.error("Error fetching survey summaries:", error);
      }
    };

    fetchSurveySummaries();
  }, []);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSurveyDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ------------------------------
  // When parliamentary mode -> fetch parliamentary constituencies by district/state
  // ------------------------------
  useEffect(() => {
    const fetchParliamentaryBoundaries = async () => {
      try {
        const stateFromLocal = constituencyDataString?.data?.state;
        const districtFromLocal = constituencyDataString?.data?.name;
        // If not present, fall back to hook data (best-effort)
        const state = (stateFromLocal || constituencyData?.data?.state || "")
          .toUpperCase();
        const district =
          districtFromLocal || constituencyData?.data?.name || "";

        if (!state || !district) return;

        const res = await axiosInstance.get(
          `/api/constituency/getAllConstituency?state=${state}&district=${district}&parliamentary=parliamentary`
        );

        const fetchedData = res?.data?.data || [];
        setParliamentaryData(fetchedData);
      } catch (err) {
        console.error("Error fetching parliamentary boundaries:", err);
      }
    };

    if (
      isLoaded &&
      constituencyDataString?.data?.type === "central"
    ) {
      fetchParliamentaryBoundaries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, constituencyDataString?.data?.state, constituencyDataString?.data?.district, constituencyDataString?.data?.type]);
  // ------------------------------
  // Choose which polygonPaths to show by default (unless approval/gms override)
  // If Approval/GMS fullscreen triggered, we display the override polygonPaths state (unchanged behavior)
  // ------------------------------
  const activePolygonPaths = (showApprovalRating || showGmsFullScreen)
    ? polygonPaths
    : constituencyDataString?.data?.type === "central"
      ? parliamentaryPolygonPaths
      : assemblyPolygonPaths;

  // ------------------------------
  // Fit map to active polygons whenever they change
  // ------------------------------
  useEffect(() => {
    if (!isLoaded) return;
    if (!activePolygonPaths || !Array.isArray(activePolygonPaths) || activePolygonPaths.length === 0) return;

    try {
      const bounds = new window.google.maps.LatLngBounds();
      activePolygonPaths.forEach((polygon) => {
        polygon?.coordinate?.forEach((point) => {
          const lat = Number(point?.lat);
          const lng = Number(point?.lng);
          if (!isNaN(lat) && !isNaN(lng)) {
            bounds.extend(new window.google.maps.LatLng(lat, lng));
          }
        });
      });

      if (!bounds.isEmpty()) {
        mapRef.current?.fitBounds(bounds);
        mapRef.current?.setCenter(bounds.getCenter());
      }
    } catch (err) {
      console.error("Error fitting bounds:", err);
    }
  }, [activePolygonPaths, isLoaded]);

  // ------------------------------
  // Fullscreen handling (unchanged)
  // ------------------------------
  useEffect(() => {
    if (isFullScreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isFullScreen]);

  const toggleFullScreen = () => {
    if (isFullScreen) {
      // exiting fullscreen: unhiding other UIs
      setShowApprovalRating(false);
      setShowGmsFullScreen(false);
      setShowSurveyAnalytics(false);
      setShowNyayePanchayat(false);
      setShowQuantitativeSurvey(false);
      setSelectedPolygon(null);

      // when exiting, if we had assembly/parliamentary polygons loaded, fit them again
      const fallbackPolygons = constituencyDataString?.data?.type === "central"
        ? parliamentaryPolygonPaths
        : assemblyPolygonPaths;

      if (fallbackPolygons && fallbackPolygons.length > 0 && mapRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        fallbackPolygons.forEach((poly) =>
          poly?.coordinate?.forEach((pt) => {
            const lat = Number(pt?.lat);
            const lng = Number(pt?.lng);
            if (!isNaN(lat) && !isNaN(lng)) bounds.extend({ lat, lng });
          })
        );
        if (!bounds.isEmpty()) mapRef.current?.fitBounds(bounds);
      }

      // The original code called window.location.reload() when showApprovalRating was truthy.
      // To preserve behavior exactly as before:
      if (showApprovalRating) window.location.reload();
    }

    setIsFullScreen((prev) => !prev);
  };

  // ------------------------------
  // Map onLoad: add India shape, set style
  // ------------------------------
  const onLoad = (map) => {
    if (!isLoaded) return;
    mapRef.current = map;
    try {
      map.data.addGeoJson(indiaShape);
      map.data.setStyle(() => ({
        fillColor: "white",
        fillOpacity: 0.1,
        strokeColor: "black",
        strokeWeight: 1,
        strokeOpacity: 0.1,
      }));
    } catch (err) {
      console.error("Error adding india geojson to map", err);
    }
  };

  // ------------------------------
  // InfoWindow/Markers (unchanged)
  // ------------------------------
  const onCloseInfoWindow = () => {
    setActiveMarker(null);
    setShowingInfoWindow(false);
  };

  function getDynamicK(pointsCount, zoom) {
    return Math.max(20, Math.floor(pointsCount / 5));
  }

  const renderClusteredMarkers = () => {
    if (!grievances || grievances.length === 0) return null;

    const points = grievances.map((g) => ({
      lat: g.geometry.coordinates[1],
      lng: g.geometry.coordinates[0],
    }));
    const zoom = mapRef.current?.getZoom() || 8;
    const k = getDynamicK(points.length, zoom);

    const { centroids, assignments } = kMeans(points, k);
    return centroids.map((c, i) => (
      <ClusterMarker
        key={`cluster-${i}`}
        position={{ lat: c.lat, lng: c.lng }}
        count={c.count}
      />
    ));
  };

  const parseLocation = (locationStr) => {
    if (!locationStr) return null;
    try {
      const cleaned = locationStr
        .replace(/[{}]/g, "")
        .replace(/lat\s*:/i, '"lat":')
        .replace(/long\s*:/i, '"lng":');
      return JSON.parse(`{${cleaned}}`);
    } catch (err) {
      console.error("Invalid location string:", locationStr);
      return null;
    }
  };

  const getVillageMarkerIcon = (fillColor = "#004877") => {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="50" viewBox="0 0 30 50">
        <circle cx="15" cy="15" r="10" fill="${fillColor}" />
        <rect x="13" y="25" width="4" height="20" fill="${fillColor}" />
      </svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(30, 50),
      anchor: new window.google.maps.Point(15, 50),
    };
  };

  const renderVillageMarkers = () => {
    if (!villageData || villageData.length === 0) return null;

    return villageData.map((village) => {
      const coords = parseLocation(village.location);
      if (!coords || isNaN(coords.lat) || isNaN(coords.lng)) return null;

      const fillColor = village?.winnerParty?.colorCode || "#004877";

      return (
        <Marker
          key={village._id}
          position={{ lat: coords.lat, lng: coords.lng }}
          icon={getVillageMarkerIcon(fillColor)}
          onClick={() => {
            setActiveMarker(village);
            setShowingInfoWindow(true);
          }}
        />
      );
    });
  };

  const renderInfoWindow = () => {
    if (activeMarker && showingInfoWindow) {
      if (activeMarker?.geometry) {
        // grievance InfoWindow
        return (
          <InfoWindow
            position={{
              lat: activeMarker.geometry.coordinates[1],
              lng: activeMarker.geometry.coordinates[0],
            }}
            onCloseClick={onCloseInfoWindow}
          >
            <MarkerInfoContent activeMarker={activeMarker} />
          </InfoWindow>
        );
      } else if (activeMarker?.villageName) {
        // village InfoWindow
        const coords = parseLocation(activeMarker.location);
        return (
          <InfoWindow
            position={{ lat: coords.lat, lng: coords.lng }}
            onCloseClick={onCloseInfoWindow}
          >
            <div>
              <h4 className="font-bold">{activeMarker.villageName}</h4>
              <p>
                Winner:{" "}
                <span style={{ color: activeMarker?.winnerParty?.colorCode }}>
                  {activeMarker?.winnerParty?.name}
                </span>
              </p>
              <p>Runner-up: {activeMarker?.runnerUpParty?.name}</p>
              <p>First Runner-up: {activeMarker?.firstRunnerUpParty?.name}</p>
            </div>
          </InfoWindow>
        );
      }
    }
    return null;
  };

  // ------------------------------
  // Render polygons based on activePolygonPaths
  // ------------------------------
  const renderConstituencyPolygons = () => {
    if (!activePolygonPaths || !Array.isArray(activePolygonPaths)) return null;

    return activePolygonPaths.map((polygon, index) => {
      const safePaths =
        polygon?.coordinate
          ?.map((point) => ({
            lat: Number(point?.lat),
            lng: Number(point?.lng),
          }))
          .filter((p) => !isNaN(p.lat) && !isNaN(p.lng)) || [];

      if (safePaths.length === 0) return null;

      return (
        <React.Fragment key={index}>
          <Polygon
            key={index}
            paths={safePaths}
            options={{
              strokeColor: "#810FCB",
              strokeOpacity: 1.0,
              strokeWeight: 2,
              fillColor: "#810FCB",
              fillOpacity: 0.5,
            }}
          />
        </React.Fragment>
      );
    });
  };

  // ------------------------------
  // Approval rating click: unchanged logic, but now writes into polygonPaths override state
  // ------------------------------
  const approvalData = [
    { area: "Bangalore South", sample: 200, approval: 40 },
    { area: "Bommanahalli", sample: 180, approval: 55 },
    { area: "Byatarayanapura", sample: 220, approval: 60 },
    { area: "C V Raman Nagar", sample: 150, approval: 35 },
    { area: "Govindaraja Nagar", sample: 250, approval: 70 },
    { area: "Krishnarajapuram", sample: 170, approval: 87 },
    { area: "Mahadevapura", sample: 220, approval: 25 },
    { area: "Malleshwaram", sample: 240, approval: 51 },
    { area: "Pulakeshinagar", sample: 120, approval: 90 },
    { area: "Rajarajeswarinagar", sample: 260, approval: 43 },
    { area: "Shanti Nagar", sample: 190, approval: 81 },
    { area: "Shivajinagar", sample: 210, approval: 28 },
    { area: "Yelahanka", sample: 220, approval: 38 },
  ];

  const handleApprovalRatingClick = async () => {
    try {
      // If already fetched approvalResponseData, simply show it (unchanged)
      if (approvalResponseData) {
        setShowApprovalRating(true);
        setIsFullScreen(true);
        return;
      }

      const response = await axiosInstance.get(
        "/api/approvalRating/getShapeFile?state=Karnataka&district=Bangalore Urban"
      );

      if (response?.data?.status && response?.data?.data?.length > 0) {
        setApprovalResponseData(response.data.data);
        const allPolygons = [];

        response?.data?.data?.forEach((constituency) => {
          const { boundaries, name } = constituency;
          if (boundaries?.type === "MultiPolygon") {
            boundaries.coordinates?.forEach((polygon) => {
              const coordinates = polygon[0]
                .map((point) => {
                  const [lng, lat] = point; // GeoJSON format
                  if (isNaN(lat) || isNaN(lng)) return null;
                  return { lat: Number(lat), lng: Number(lng) };
                })
                .filter(Boolean);

              if (coordinates.length > 0) {
                allPolygons.push({
                  coordinate: coordinates,
                  name,
                });
              }
            });
          } else if (boundaries?.type === "Polygon") {
            // Support regular Polygon too
            const coordinates = boundaries.coordinates?.[0]
              ?.map((point) => {
                const [lng, lat] = point;
                if (isNaN(lat) || isNaN(lng)) return null;
                return { lat: Number(lat), lng: Number(lng) };
              })
              .filter(Boolean);

            if (coordinates && coordinates.length > 0) {
              allPolygons.push({
                coordinate: coordinates,
                name,
              });
            }
          }
        });

        // override polygons (same as before)
        setIsFullScreen(true);
        setPolygonPaths(allPolygons);
        setShowApprovalRating(true);

        // fit map to new polygons
        const bounds = new window.google.maps.LatLngBounds();
        allPolygons?.forEach((poly) => {
          poly.coordinate?.forEach((pt) => {
            if (!isNaN(Number(pt.lat)) && !isNaN(Number(pt.lng))) {
              bounds.extend({ lat: Number(pt.lat), lng: Number(pt.lng) });
            }
          });
        });
        if (!bounds.isEmpty()) {
          mapRef.current?.fitBounds(bounds);
        }
      }
    } catch (error) {
      console.error("Error fetching approval rating shape file:", error);
    }
  };

  const handleSurveyAnalyticsClick = async (surveyId) => {
    try {
      if (!surveyId) return;

      const response = await axiosInstance.get(
        `/api/surveyAnalytics/getShapeFile/${surveyId}`
      );

      if (response?.data?.status && response?.data?.data?.length > 0) {
        setSurveyResponseData(response.data.data);

        const allPolygons = buildPolygonPaths(response.data.data);

        setIsFullScreen(true);
        setPolygonPaths(allPolygons);
        setShowSurveyAnalytics(true);

        const bounds = new window.google.maps.LatLngBounds();
        allPolygons.forEach((poly) => {
          poly.coordinate.forEach((point) => bounds.extend(point));
        });

        mapRef.current?.fitBounds(bounds);
      }
    } catch (error) {
      console.error("Error fetching survey analytics shape file:", error);
    }
  };

  const handleNyayePanchayatClick = async () => {
  try {
    const response = await axiosInstance.get(
      `/api/nyayePanchayat/getBlockShape`
    );

    if (response?.data?.status && response?.data?.data) {
      const geoData = response.data.data;

      const { constituency, blocks = [], villages = [] } = geoData;

      const allPolygons = [];

      // -----------------------------
      // 🟩 BLOCKS (colored)
      // -----------------------------
      blocks.forEach((feature) => {
        const { geometry, properties } = feature;

        if (!geometry) return;

        extractPolygonRings(geometry).forEach((poly) => {
          const ring = poly[0];

          const coords = ring.map(([lng, lat]) => ({
            lat: Number(lat),
            lng: Number(lng),
          }));

          allPolygons.push({
            path: coords,
            name: properties?.block_name,
            color: properties?.color,
            type: "block",
          });
        });
      });

      // -----------------------------
      // VILLAGES (thin borders)
      // -----------------------------
      villages.forEach((feature) => {
        const { geometry, properties } = feature;

        if (!geometry) return;

        extractPolygonRings(geometry).forEach((poly) => {
          const ring = poly[0];

          const coords = ring.map(([lng, lat]) => ({
            lat: Number(lat),
            lng: Number(lng),
          }));

          allPolygons.push({
            path: coords,
            name: properties?.name,
            blockName: properties?.block_name,
            nyayPanchayat: properties?.nyayPanchayat,
            type: "village",
          });
        });
      });

      // -----------------------------
      // CONSTITUENCY (outer border)
      // -----------------------------
      // if (constituency?.geometry) {
      //   constituency.geometry.coordinates.forEach((poly) => {
      //     const ring = poly[0];

      //     const coords = ring.map(([lng, lat]) => ({
      //       lat: Number(lat),
      //       lng: Number(lng),
      //     }));

      //     allPolygons.push({
      //       path: coords,
      //       type: "constituency",
      //     });
      //   });
      // }
      console.log("allPolygons",allPolygons)
      // -----------------------------
      // SET STATE
      // -----------------------------
      setNyayePolygonPaths(allPolygons);
      setPolygonPaths(allPolygons); // reuse existing renderer
      setShowNyayePanchayat(true);
      setIsFullScreen(true);

      // -----------------------------
      // FIT BOUNDS
      // -----------------------------
      const bounds = new window.google.maps.LatLngBounds();

      allPolygons.forEach((poly) =>
        poly.path.forEach((p) => bounds.extend(p))
      );

      if (!bounds.isEmpty()) {
        mapRef.current?.fitBounds(bounds);
      }
    }
  } catch (err) {
    console.error("Nyaye Panchayat Error:", err);
  }
};

  const handleQuantitativeSurveyClick = async () => {
    try {
      const clientId = userData?._id || userData?.organisationId;
      if (!clientId) return;

      const response = await axiosInstance.get(
        `/api/quantitativeSurvey/getShapes`,
        { params: { clientId } }
      );

      if (response?.data?.status && Array.isArray(response?.data?.data)) {
        const features = response.data.data;
        const allPolygons = [];

        features.forEach((feature) => {
          const { geometry, properties } = feature;
          if (!geometry) return;

          const rings =
            geometry.type === "Polygon"
              ? [geometry.coordinates]
              : geometry.type === "MultiPolygon"
              ? geometry.coordinates
              : [];

          rings.forEach((poly) => {
            const ring = poly[0];
            if (!ring) return;
            const coords = ring.map(([lng, lat]) => ({
              lat: Number(lat),
              lng: Number(lng),
            }));
            allPolygons.push({
              path: coords,
              name: properties?.ward_name || properties?.name || "Unknown",
              type: "ward",
            });
          });
        });

        setQuantitativeSurveyPolygonPaths(allPolygons);
        setShowQuantitativeSurvey(true);
        setIsFullScreen(true);

        const bounds = new window.google.maps.LatLngBounds();
        allPolygons.forEach((poly) => poly.path.forEach((p) => bounds.extend(p)));
        if (!bounds.isEmpty()) mapRef.current?.fitBounds(bounds);
      }
    } catch (err) {
      console.error("Quantitative Survey Error:", err);
    }
  };

  const handleTotalGmsClick = () => {
    setIsFullScreen(true);
    setShowGmsFullScreen(true);
  };

  // ------------------------------
  // Load handler: we keep it lightweight; main fitBounds logic is handled by effects
  // ------------------------------
  if (loadError) {
    return <div className="loader">Error loading the map</div>;
  }

  if (isLoaded) {
    return (
      <>
        <div className={`lg:col-span-2 ${isFullScreen ? "space-y-6" : ""} `}>
          <div
            className={
              isFullScreen
                ? "fixed inset-0 z-[9999] h-[97%] w-[98%] mx-2 my-2"
                : "h-[100%]"
            }
          >
            <div
              className={`relative h-[100%] bg-white/20 border border-white/30 rounded-3xl overflow-hidden shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] ${
                isFullScreen
                  ? " !bg-black !bg-opacity-90 !border-none transition-all duration-300 ease-in-out"
                  : "transition-all duration-300 ease-in-out"
              }`}
            >
              {/* Fullscreen Button */}
              <Tooltip title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}>
                <Button
                  icon={isFullScreen ? <CloseOutlined /> : <FullscreenOutlined />}
                  onClick={() => {
                    toggleFullScreen();
                    setIsActive((prev) => !prev);
                  }}
                  shape="circle"
                  size="large"
                  className={`handle-btn absolute z-[10] transition-none !bg-[#00487] ${
                    isActive ? "top-4 right-4" : "top-4 right-4"
                  }`}
                />
              </Tooltip>

              <div className="w-full h-full flex items-center justify-center bg-white/20  relative">
                <div className="text-center h-[100%] w-[100%]">
                  <GoogleMap
                    zoom={initialZoom}
                    mapContainerClassName={`map-container w-full ${
                      isFullScreen ? "h-screen" : "h-[500px]"
                    }`}
                    options={{
                      styles: themes === "darkMode" ? mapStyle : mapStyles,
                      fullscreenControl: false,
                      mapTypeControl: false,
                      restriction: {
                        latLngBounds: indiaBounds,
                        strictBounds: true,
                      },
                    }}
                    onLoad={onLoad}
                  >
                    {showApprovalRating ? (
                      <ApprovalRatingMap
                        mapRef={mapRef}
                        showApprovalRating={showApprovalRating}
                        polygonPaths={polygonPaths}
                        approvalData={approvalData}
                        approvalResponseData={approvalResponseData}
                        setPolygonPaths={setPolygonPaths}
                      />
                    ) : showGmsFullScreen ? (
                      <GmsFullScreenMap
                        mapRef={mapRef}
                        grievances={grievances}
                        polygonPaths={polygonPaths}
                        villageData={villageData}
                      />
                    ) : showSurveyAnalytics ? (   
                      <SurveyAnalyticsMap
                        mapRef={mapRef}
                        showSurveyAnalytics={showSurveyAnalytics}
                        polygonPaths={polygonPaths}
                        // surveyId={userData?.surveyId}
                        surveyId={selectedSurveyId}
                      />
                    ) : showNyayePanchayat ? (
                      <NyayePanchayatMap
                        mapRef={mapRef}
                        polygonPaths={nyayePolygonPaths}
                      />
                    ) : showQuantitativeSurvey ? (
                      <QuantitativeSurveyMap
                        mapRef={mapRef}
                        polygonPaths={quantitativeSurveyPolygonPaths}
                        clientId={userData?._id || userData?.organisationId}
                      />
                    ) : (
                      <>
                        {renderVillageMarkers()}
                        {renderConstituencyPolygons()}
                        {renderInfoWindow()}
                      </>
                    )}

                  </GoogleMap>
                </div>

                {/* top-left quick action buttons (shown when not in approval/gms full screen) */}
                {(!showApprovalRating && !showGmsFullScreen && !showSurveyAnalytics && !showNyayePanchayat && !showQuantitativeSurvey) && (
                  <div className="absolute top-4 left-4 flex gap-4">
                    {/* Total GMS */}
                    <div
                      onClick={handleTotalGmsClick}
                      className="cursor-pointer bg-glass backdrop-blur-[40px] border border-white/10 rounded-2xl px-6 py-3 shadow-lg hover:bg-[#3bbee8]/20 transition"
                    >
                      <div className="text-center">
                        <div className="text-sm font-semibold text-[#004877] map-btn">
                          {t("Dashboard.totalGMS")} : <span className="text-[#000]">{grievances?.length}</span>
                        </div>
                      </div>
                    </div>

                    {/* Social Media */}
                    {/* <div className="bg-glass backdrop-blur-[40px] border border-white/10 rounded-2xl px-6 py-3 shadow-lg hover:bg-white/60 transition">
                      <div className="text-center">
                        <div className="text-sm font-semibold text-[#004877]">
                          Social Media : <span className="text-[#000]">10</span>
                        </div>
                      </div>
                    </div> */}

                    {/* Approval Rating */}
                    {userData?.showApprovalRating && 
                      <div
                        onClick={handleApprovalRatingClick}
                        className="cursor-pointer bg-glass backdrop-blur-[40px] border border-white/10 rounded-2xl px-6 py-3 shadow-lg hover:bg-[#3bbee8]/20 transition"
                      >
                        <div className="text-center">
                          <div className="text-sm font-semibold map-btn">
                            {t("Dashboard.approvalRating")} : <span className="text-[#000]">64%</span>
                          </div>
                        </div>
                      </div>
                    }
              
                    {surveySummaries.length === 1 && (
                      <div
                        onClick={() => handleSurveyAnalyticsClick(selectedSurveyId)}
                        className="cursor-pointer bg-glass backdrop-blur-[40px] border border-white/10 rounded-2xl px-6 py-3 shadow-lg hover:bg-[#3bbee8]/20 transition"
                      >
                        <div className="text-center">
                          <div className="text-sm font-semibold map-btn">
                            {surveySummaries[0]?.surveyName}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* <div
                      onClick={handleNyayePanchayatClick}
                      className="cursor-pointer bg-glass backdrop-blur-[40px] border border-white/10 rounded-2xl px-6 py-3 shadow-lg hover:bg-[#3bbee8]/20 transition"
                    >
                      <div className="text-center">
                        <div className="text-sm font-semibold map-btn">
                         {t("Dashboard.nyayPanchayat")}
                        </div>
                      </div>
                    </div> */}

                    {userData?.showQuantitativeSurvey && (
                      <div
                        onClick={handleQuantitativeSurveyClick}
                        className="cursor-pointer bg-glass backdrop-blur-[40px] border border-white/10 rounded-2xl px-6 py-3 shadow-lg hover:bg-[#3bbee8]/20 transition"
                      >
                        <div className="text-center">
                          <div className="text-sm font-semibold map-btn">
                            Quantitative Survey
                          </div>
                        </div>
                      </div>
                    )}

                    {surveySummaries.length > 1 && (
                      <div className="relative" ref={dropdownRef}>
                        
                        {/* Button */}
                        <div
                          onClick={() => setShowSurveyDropdown((prev) => !prev)}
                          className="cursor-pointer bg-glass backdrop-blur-[40px] border border-white/10 rounded-2xl px-6 py-3 shadow-lg hover:bg-[#3bbee8]/20 transition flex items-center gap-3"
                        >
                          <div className="text-sm font-semibold map-btn">
                            {selectedSurveyId
                              ? surveySummaries.find(s => s._id === selectedSurveyId)?.surveyName
                              : t("Dashboard.selectSurvey")}
                          </div>

                          <DownOutlined
                            className={`transition-transform duration-300 ${
                              showSurveyDropdown ? "rotate-180" : ""
                            }`}
                          />
                        </div>

                        {/* Dropdown */}
                        {showSurveyDropdown && (
                          <div className="absolute mt-3 w-64 bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden animate-fadeIn">
                            
                            {surveySummaries.map((survey) => {
                              const isSelected = survey._id === selectedSurveyId;

                              return (
                                <div
                                  key={survey._id}
                                  onClick={() => {
                                    setSelectedSurveyId(survey._id);
                                    setShowSurveyDropdown(false);
                                    handleSurveyAnalyticsClick(survey._id);
                                  }}
                                  className={`px-5 py-3 cursor-pointer text-sm transition-all duration-200 flex justify-between items-center
                                    ${
                                      isSelected
                                        ? "bg-[#3bbee8]/20 text-[#004877] font-semibold"
                                        : "hover:bg-gray-100 text-gray-700"
                                    }`}
                                >
                                  <span>{survey.surveyName}</span>

                                  {isSelected && (
                                    <span className="text-xs text-[#004877]">✓</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Overlay Constituency Details */}
                {(!showApprovalRating && !showGmsFullScreen && !showSurveyAnalytics && !showQuantitativeSurvey && !showNyayePanchayat) && (
                  <div className="absolute bottom-4 left-4 bg-glass backdrop-blur-[40px] border border-white/40 rounded-3xl p-1 shadow-[0_6px_24px_0_rgba(31,38,135,0.25)] max-w-xs">
                    <div className="text-center mb-3">
                      <div className="text-xl font-bold text-[#3bbee8]">
                        {constituencyDataString?.data?.name}
                      </div>
                      <div className="text-sm subheading">{constituencyDataString?.data?.type==='state'?"Assembly":"Parliamentary"}</div>
                      <div className="text-sm font-semibold heading-white">
                        {constituencyDataString?.data?.district}, {constituencyDataString?.data?.state}
                      </div>
                    </div>

                    <div className="bg-white/20 backdrop-blur-[30px] rounded-t-2xl px-10 py-[0.1rem]">
                      <div className="grid grid-cols-2 text-center">
                        <div>
                          <span className="text-xs subheading">{constituencyData?.type==='state'?"Wards":"Assemblies"}</span>
                          <div className="text-sm font-bold text-blue-500">{wards}</div>
                        </div>
                        <div>
                          <span className="text-xs subheading">Polling Stations</span>
                          <div className="text-sm font-bold text-gray-800">{ps}</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/20 backdrop-blur-[30px] rounded-t-2xl px-10 py-[0.1rem]">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <span className="text-xs subheading">Winner</span>
                          <div className="text-sm font-bold" style={{color:summary?.partyColorCode}}>{winner}</div>
                        </div>
                        <div>
                          <span className="text-xs subheading">Votes</span>
                          <div className="text-sm font-bold text-gray-800">{votes}</div>
                        </div>
                        <div>
                          <span className="text-xs subheading">Share%</span>
                          <div className="text-sm font-bold text-gray-800">{share}%</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/20 backdrop-blur-[30px] rounded-t-2xl px-10 py-[0.1rem]">
                      <div className="grid grid-cols-2 gap-[4rem] text-center">
                        <div>
                          <span className="text-xs subheading">{constituencyData?.type==='state'?"Wards Won":"Assemblies Won"}</span>
                          <div className="text-sm font-bold text-gray-800">{wardsWon}</div>
                        </div>
                        <div>
                          <span className="text-xs subheading">PS Won</span>
                          <div className="text-sm font-bold text-gray-800">{psWon}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return <div className="loader">Loading...</div>;
};

export default OverviewMap;
